import clsx from "clsx";
import { FormControlError } from "../../atoms/FormFiledError";
import { DateInput, FormInput } from "../../atoms/Input/Input";
import { FormLabel, FormLabelWithIcon } from "../../atoms/label/label";
import { FormOption, FormSelect } from "../../atoms/select";
import { FormTextArea } from "../../atoms/textArea";
import { GenericFormSelectAPI } from "../../molecules/select";
import { OPTIONS_MAPPINGS } from "./utils";
import { Switch, Radio, Select } from "antd";
import { StyledFormRadioDiv, StyledFormSwitchDiv } from "../../atoms/Input/Input.styles";
import { useSelectContext } from "../../../hooks/useSelectContext";
import { GenericFormSearchableSelectAPI } from "../../molecules/select/select";
import { DownArrow } from "../../../assets/icons";
import { StyledSelectAntDiv } from "../../atoms/select/select.styles";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { StyledPhoneContainer } from "../../atoms/Input/Input.styles";
import { useMemo } from "react";
import { getIn } from "formik";
import { CustomTooltip } from "../Tooltip/Tooltip";
import { Info } from "../../../assets/icons";

/**
 * <FormControlInput>
 *
 * Controlled text input tied to Formik context. Renders label, input field,
 * and error message automatically.
 *
 * Props
 * - name (string): form field name
 * - type (string): HTML input type (e.g., "text", "number")
 * - label (string): label text to display above input
 * - placeholder (string): placeholder text for input
 * - readOnly (boolean): whether input is read-only (default: false)
 * - required (boolean): whether field is required (default: false)
 *
 * Returns
 * - ReactElement: labeled text input with error handling
 */
export const FormControlInput = ({
  name,
  type,
  label = "",
  hoverText = "",
  placeholder = "",
  readOnly = false,
  required = false,
  disabled = false,
  context = "master",
  variant,
}) => {
  const {
    formik: { getFieldProps, touched, errors },
  } = useSelectContext(context);
  return (
    <>
      {label && (
        <FormLabelWithIcon label={label} hoverText={hoverText} required={required} name={name} />
      )}
      <FormInput
        {...getFieldProps(name)}
        aria-describedby={`${name}-error`}
        id={name}
        placeholder={placeholder}
        readOnly={readOnly}
        type={type}
        disabled={disabled}
        variant={variant}
      />
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * <FormControlTextArea>
 *
 * Controlled textarea tied to Formik context. Renders label, textarea, and
 * error message automatically.
 *
 * Props
 * - name (string): form field name
 * - label (string): label text to display above textarea
 * - placeholder (string): placeholder text for textarea
 * - required (boolean): whether field is required (default: false)
 *
 * Returns
 * - ReactElement: labeled textarea with error handling
 */
export const FormControlTextArea = ({
  name,
  label = "",
  placeholder = "",
  required = false,
  rows = 2,
  context = "master",
  variant,
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors },
  } = useSelectContext(context);

  return (
    <>
      <FormLabel className={clsx({ req: required })} htmlFor={name}>
        {label}
      </FormLabel>
      <FormTextArea
        {...getFieldProps(name)}
        aria-describedby={`${name}-error`}
        id={name}
        placeholder={placeholder}
        rows={rows}
        variant={variant}
        {...props}
      />
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * <FormControlSelect>
 *
 * Controlled select dropdown tied to Formik context. Uses static option
 * mappings provided via optionKey.
 *
 * Props
 * - name (string): form field name
 * - optionKey (string): key to OPTIONS_MAPPINGS for available options
 * - label (string): label text to display above select
 * - multiple (boolean): whether multiple selections are allowed (default: false)
 * - placeholder (string): placeholder text when no value selected
 * - required (boolean): whether field is required (default: false)
 * - ...props: Extra props to be injected into select
 *
 * Returns
 * - ReactElement: labeled select dropdown with mapped options and error handling
 */
export const FormControlSelect = ({
  name,
  optionKey,
  label = "",
  hoverText = "",
  multiple = false,
  placeholder = "",
  required = false,
  context = "master",
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue, setFieldTouched, values },
  } = useSelectContext(context);

  const options = OPTIONS_MAPPINGS[optionKey];
  if (!options) throw new Error(`Options not available for: ${optionKey}`);

  if (multiple) {
    const current = Array.isArray(values[name]) ? values[name] : [];
    return (
      <>
        {label && (
          <FormLabelWithIcon label={label} hoverText={hoverText} required={required} name={name} />
        )}
        <StyledSelectAntDiv className="multiple">
          <Select
            id={name}
            aria-describedby={`${name}-error`}
            status={touched[name] && errors[name] ? "error" : undefined}
            onBlur={() => setFieldTouched(name, true)}
            mode="multiple"
            allowClear
            showSearch
            placeholder={placeholder}
            value={current}
            onChange={val => {
              setFieldTouched(name, true, false);
              setFieldValue(name, Array.isArray(val) ? val : []);
            }}
            options={options.map(item => ({ value: item.id, label: item.name }))}
            suffixIcon={<DownArrow />}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: "100%" }}
            {...props}
          />
        </StyledSelectAntDiv>
        <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
      </>
    );
  }

  return (
    <>
      {label && (
        <FormLabelWithIcon label={label} hoverText={hoverText} required={required} name={name} />
      )}

      <FormSelect
        {...getFieldProps(name)}
        {...props}
        aria-describedby={`${name}-error`}
        id={name}
        multiple={multiple}
        placeholder={placeholder}
      >
        {placeholder && (
          <FormOption key="noValue" value="">
            {placeholder}
          </FormOption>
        )}
        {options.map(item => (
          <FormOption
            className={clsx({
              __other__: item.id === "__other__",
            })}
            key={item.id}
            value={item.id}
          >
            {item.name}
          </FormOption>
        ))}
      </FormSelect>
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * <FormControlSelectAPI>
 *
 * Controlled select dropdown tied to Formik context. Dynamically fetches
 * options from API using the provided moduleName.
 *
 * Props
 * - name (string): form field name
 * - moduleName (string): API module to fetch options from
 * - label (string): label text to display above select
 * - multiple (boolean): whether multiple selections are allowed (default: false)
 * - placeholder (string): placeholder text when no value selected
 * - required (boolean): whether field is required (default: false)
 *
 * Returns
 * - ReactElement: labeled select dropdown backed by API options with error handling
 */
export const FormControlSelectAPI = ({
  name,
  moduleName,
  label = "",
  multiple = false,
  placeholder = "",
  required = false,
  activeRecords = false,
  readOnly = false,
  params,
  skip = false,
  context = "master",
}) => {
  const {
    formik: { getFieldProps, touched, errors },
  } = useSelectContext(context);

  return (
    <>
      {label && (
        <FormLabel className={clsx({ req: required })} htmlFor={name}>
          {label}
        </FormLabel>
      )}
      <GenericFormSelectAPI
        {...getFieldProps(name)}
        aria-describedby={`${name}-error`}
        id={name}
        moduleName={moduleName}
        multiple={multiple}
        placeholder={placeholder}
        activeRecords={activeRecords}
        disabled={readOnly}
        params={params}
        skip={skip}
      />
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * <FormControlSearchableSelectAPI>
 *
 * Controlled searchable select dropdown integrated with Formik context.
 * Dynamically fetches options from API using the provided moduleName and
 * allows users to search, select, and clear values efficiently.
 *
 * Features
 * - Fetches options dynamically from API based on moduleName
 * - Supports searchable dropdown with client-side filtering
 * - Allows single or multiple selection (AntD Select modes)
 * - Supports clearing selected value(s)
 * - Filters only active records when activeRecords is true
 * - Handles loading and error states gracefully
 * - Automatically binds value and onChange with Formik using context
 * - Custom option transformation using module config (prepare function)
 *
 * Props
 * - name (string): form field name (Formik field)
 * - moduleName (string): API module to fetch options from
 * - label (string): label text to display above select
 * - multiple (boolean): enables multi-select mode (default: false)
 * - placeholder (string): placeholder text when no value selected
 * - required (boolean): marks label as required (default: false)
 * - activeRecords (boolean): filters only active records from API response
 * - readOnly (boolean): disables the select field (default: false)
 * - params (object): additional params passed to API query
 * - context (string): Formik context key (default: "master")
 * - ...props: additional props passed to AntD Select component
 *
 */

export const FormControlSearchableSelectAPI = ({
  name,
  moduleName,
  label = "",
  hoverText = "",
  multiple = false,
  placeholder = "",
  required = false,
  activeRecords = false,
  readOnly = false,
  params,
  skip = false,
  context = "master",
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors },
  } = useSelectContext(context);

  return (
    <>
      {label && (
        <FormLabel className={clsx({ req: required })} htmlFor={name}>
          {label}
        </FormLabel>
      )}
      <GenericFormSearchableSelectAPI
        {...getFieldProps(name)}
        aria-describedby={`${name}-error`}
        id={name}
        moduleName={moduleName}
        placeholder={placeholder}
        activeRecords={activeRecords}
        disabled={readOnly}
        params={params}
        skip={skip}
        context={context}
        name={name}
        mode={multiple ? "multiple" : undefined}
        {...props}
      />
      <FormControlError
        id={`${name}-error`}
        errorMsg={getIn(touched, name) && getIn(errors, name)}
      />
    </>
  );
};

export const FormControlSearchableSelect = ({
  name,
  moduleName,
  label = "",
  hoverText = "",
  multiple = false,
  placeholder = "",
  required = false,
  activeRecords = false,
  readOnly = false,
  params,
  context = "master",
  options = [],
  optionKey,
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue },
  } = useSelectContext(context);

  const finalOptions = useMemo(
    () =>
      OPTIONS_MAPPINGS[optionKey]?.map(item => ({ label: item.name, value: item.id })) || options,

    [optionKey, options],
  );

  const handleChange = value => {
    setFieldValue(name, value);
  };

  return (
    <>
      {label && (
        <FormLabel className={clsx({ req: required })} htmlFor={name}>
          {label}
        </FormLabel>
      )}

      <StyledSelectAntDiv className={multiple ? "multiple" : ""}>
        <Select
          {...getFieldProps(name)}
          suffixIcon={<DownArrow />}
          showSearch
          allowClear
          style={{ width: "100%" }}
          placeholder={placeholder || "Please Select"}
          onChange={handleChange}
          options={finalOptions}
          mode={multiple ? "multiple" : undefined}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          {...props}
        />
      </StyledSelectAntDiv>
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * FormControlSwitch
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.left - Content to render on the left side of the switch (e.g., label or text).
 * @param {React.ReactNode} props.right - Content to render on the right side of the switch (e.g., label or text).
 * @param {function} props.handleChange - Callback function triggered when the switch value changes.
 * @param {Object} [props.props] - Additional props spread onto the underlying `Switch` component.
 *
 * @returns {JSX.Element} A styled toggle switch with optional left and right labels/content.
 *
 * @description
 * The `FormControlSwitch` component renders a switch toggle with optional content (text, icons, or labels)
 * on its left and right. It is useful for settings or boolean options where context labels are needed.
 *
 */

export const FormControlSwitch = ({ left, right, handleChange, ...props }) => {
  return (
    <StyledFormSwitchDiv className="formControlSwitch" size={props?.size}>
      {left}
      <Switch onChange={handleChange} {...props} />
      {right}
    </StyledFormSwitchDiv>
  );
};

/**
 * FormControlDate
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.name - Field name connected to Formik form state.
 * @param {string} [props.label=""] - Label text displayed above the date input.
 * @param {boolean} [props.required=false] - Marks the label with a required indicator.
 * @param {...any} props - Additional props passed to the DateInput component.
 *
 * @returns {JSX.Element} A labeled date input field integrated with Formik, including validation error display.
 *
 * @description
 * This component renders a date picker input within a form.
 * It is connected to Formik form state via `useManageMasterContext`.
 * It displays a label, the date picker, and validation errors if present.
 */

export const FormControlDate = ({
  name,
  label = "",
  required = false,
  context = "master",
  siteTimezone,
  companyTimezone,
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue, setTouched },
  } = useSelectContext(context);
  const { value } = getFieldProps(name);

  const handleDateInput = (name, date) => {
    setTouched({ ...touched, [name]: true });
    setFieldValue(name, date, true);
  };

  return (
    <>
      <FormLabel className={clsx({ req: required })} htmlFor={name}>
        {label}
      </FormLabel>
      <DateInput
        aria-describedby={`${name}-error`}
        id={name}
        value={value}
        inputName={name}
        handleDateInput={handleDateInput}
        siteTimezone={siteTimezone}
        companyTimezone={companyTimezone}
        {...props}
      />
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * FormControlRadioGroup
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.name - Field name connected to Formik form state.
 * @param {string} [props.label=""] - Label text displayed above the radio group.
 * @param {boolean} [props.required=false] - Marks the label with a required indicator.
 * @param {Array} props.options - Radio options array with { label, value } shape.
 * @param {...any} props - Additional props passed to the Radio.Group component.
 *
 * @returns {JSX.Element} A labeled radio group integrated with Formik, including validation error display.
 */

export const FormControlRadioGroup = ({
  name,
  label = "",
  options = [],
  required = false,
  context = "master",
  className = "",
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue },
  } = useSelectContext(context);
  const { value } = getFieldProps(name);

  return (
    <>
      {label && (
        <FormLabel className={clsx({ req: required })} htmlFor={name}>
          {label}
        </FormLabel>
      )}
      <StyledFormRadioDiv className={className}>
        <Radio.Group
          aria-describedby={`${name}-error`}
          id={name}
          name={name}
          value={value}
          onChange={e => setFieldValue(name, e.target.value)}
          {...props}
        >
          {options.map(opt => {
            const RadioComponent = props.optionType === "button" ? Radio.Button : Radio;
            return (
              <RadioComponent key={opt.value} value={opt.value} disabled={opt.disabled}>
                <span className="d-flex align-items-center gap-1">
                  {opt.label}
                  {opt.hoverText && (
                    <CustomTooltip text={opt.hoverText}>
                      <Info style={{ width: 14, height: 14 }} />
                    </CustomTooltip>
                  )}
                </span>
              </RadioComponent>
            );
          })}
        </Radio.Group>
      </StyledFormRadioDiv>
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};

/**
 * FormControlPhone
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.name - Field name connected to Formik form state.
 * @param {string} [props.label=""] - Label text displayed above.
 * @param {boolean} [props.required=false] - Marks the label with a required indicator.
 * @param {...any} props - Additional props passed to PhoneInput.
 *
 * @returns {JSX.Element} A labeled phone input integrated with Formik.
 */
export const FormControlPhone = ({
  name,
  label = "",
  hoverText = "",
  placeholder = "",
  readOnly = false,
  required = false,
  disabled = false,
  context = "master",
  defaultCountry = "NZ",
  ...props
}) => {
  const {
    formik: { getFieldProps, setFieldValue, setFieldTouched, touched, errors },
  } = useSelectContext(context);
  const { value } = getFieldProps(name);

  return (
    <>
      {label && (
        <FormLabelWithIcon label={label} hoverText={hoverText} required={required} name={name} />
      )}
      <StyledPhoneContainer>
        <PhoneInput
          international
          defaultCountry={defaultCountry}
          value={value}
          onChange={val => setFieldValue(name, val)}
          onBlur={() => setFieldTouched(name, true)}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          id={name}
          {...props}
        />
      </StyledPhoneContainer>
      <FormControlError id={`${name}-error`} errorMsg={touched[name] && errors[name]} />
    </>
  );
};
